// PM-curated modality tag list for the onboarding modality picker.
// Independent of the creator about-page `PROFILE_KEYWORDS`: this list is owned
// by onboarding and its selection is stored as an onboarding response, not on
// `Creator.keywords`. Source: "Modality Picker Tags & Categories - May 2026".

// Display tier controls above-the-fold visibility. `Primary Category` tags are
// shown initially; `Show All` tags appear once the picker is expanded.
type ModalityTagTier = 'primary' | 'showAll';

type ModalityTag = {
  label: string;
  tier: ModalityTagTier;
};

// Ordered #1–49 from the CSV. The 12 `primary` tags lead, in display order.
const MODALITY_TAGS: readonly ModalityTag[] = [
  { label: 'Yoga', tier: 'primary' },
  { label: 'Pilates', tier: 'primary' },
  { label: 'Pre/post natal', tier: 'primary' },
  { label: 'Strength', tier: 'primary' },
  { label: 'Crossfit', tier: 'primary' },
  { label: 'Endurance', tier: 'primary' },
  { label: 'HIIT', tier: 'primary' },
  { label: 'Barre', tier: 'primary' },
  { label: 'Sports', tier: 'primary' },
  { label: 'Nutrition & Recipes', tier: 'primary' },
  { label: 'Dance Fitness', tier: 'primary' },
  { label: 'Outdoor', tier: 'primary' },
  { label: 'Meditation', tier: 'showAll' },
  { label: 'Martial Arts', tier: 'showAll' },
  { label: 'Cycling', tier: 'showAll' },
  { label: 'Swimming', tier: 'showAll' },
  { label: '6-pack', tier: 'showAll' },
  { label: 'Abs and Core', tier: 'showAll' },
  { label: 'Athletic', tier: 'showAll' },
  { label: 'Basketball', tier: 'showAll' },
  { label: 'Beginner', tier: 'showAll' },
  { label: 'Bikini Body', tier: 'showAll' },
  { label: 'Bodyweight', tier: 'showAll' },
  { label: 'Booty', tier: 'showAll' },
  { label: 'Boxing', tier: 'showAll' },
  { label: 'Build muscle', tier: 'showAll' },
  { label: 'Cardio', tier: 'showAll' },
  { label: 'Celebrity', tier: 'showAll' },
  { label: 'Classes', tier: 'showAll' },
  { label: 'Coaching', tier: 'showAll' },
  { label: 'Conditioning', tier: 'showAll' },
  { label: 'Flexibility', tier: 'showAll' },
  { label: 'Full Body', tier: 'showAll' },
  { label: 'Functional', tier: 'showAll' },
  { label: 'Home', tier: 'showAll' },
  { label: 'Kettlebell', tier: 'showAll' },
  { label: 'Lean muscle', tier: 'showAll' },
  { label: 'Mobility', tier: 'showAll' },
  { label: 'Mom', tier: 'showAll' },
  { label: 'No equipment', tier: 'showAll' },
  { label: 'Performance', tier: 'showAll' },
  { label: 'Recovery', tier: 'showAll' },
  { label: 'Shred fat', tier: 'showAll' },
  { label: 'Soccer', tier: 'showAll' },
  { label: 'Stretching', tier: 'showAll' },
  { label: 'Tabata', tier: 'showAll' },
  { label: 'Toning', tier: 'showAll' },
  { label: 'Weight lifting', tier: 'showAll' },
  { label: 'Weight loss', tier: 'showAll' },
  // Deliberately last: the catch-all for non-fitness creators. Selecting it
  // fails ICP fit in the booking classification (the backend excludes
  // 'other', case-insensitively), so such creators skip the book-a-call step.
  { label: 'Other', tier: 'showAll' },
];

// Full list of labels in display order — the search/show-all corpus.
export const MODALITY_TAG_LABELS: readonly string[] = MODALITY_TAGS.map(
  (tag) => tag.label,
);

// The 12 `primary`-tier labels shown above the fold before "show all".
export const PRIMARY_MODALITY_TAG_LABELS: readonly string[] =
  MODALITY_TAGS.filter((tag) => tag.tier === 'primary').map((tag) => tag.label);

// Max tags a creator may select in the onboarding modality picker. Onboarding
// owns this independently of the profile-side `KEYWORD_LIMIT`.
export const MODALITY_SELECTION_LIMIT = 5;
